import React, { useState } from "react";
import {
  Briefcase, Edit3, Save, X, Github, Linkedin, Globe, Code2, Link2,
  MapPin, Sparkles,
} from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppCardHeader, AppCardTitle,
  Callout, Checkbox, FormField, Input, NativeSelect, Textarea, AppBadge,
} from "../ui";
import SkillsField from "./SkillsField";
import NavChip from "./NavChip";
import { ROLE_FAMILIES, SENIORITY_LEVELS, labelFor } from "./constants";
import { saveCareerProfile, errorText } from "./portfolioApi";

/**
 * CareerProfileCard — the career half of the portfolio, view and edit.
 *
 * ⚠️ This is not "extra profile fields". Target role, seniority, years and
 * skills are the INPUT to the resume analysis — they are what turns a generic
 * formatting critique into "your resume does not mention SQL and this role is
 * built on it". The card says so out loud, because a student who skips it gets a
 * markedly worse report and has no way to know why.
 */

const EMPTY_LINKS = { github: "", linkedin: "", website: "", leetcode: "", other: "" };

const LINK_FIELDS = [
  { key: "github", label: "GitHub", icon: Github, placeholder: "https://github.com/username" },
  { key: "linkedin", label: "LinkedIn", icon: Linkedin, placeholder: "https://linkedin.com/in/username" },
  { key: "website", label: "Portfolio site", icon: Globe, placeholder: "https://yourname.dev" },
  { key: "leetcode", label: "LeetCode / coding profile", icon: Code2, placeholder: "https://leetcode.com/username" },
  { key: "other", label: "Other link", icon: Link2, placeholder: "https://…" },
];

const toForm = (cp = {}) => ({
  headline: cp.headline || "",
  summary: cp.summary || "",
  targetRole: cp.targetRole || "",
  targetRoleFamily: cp.targetRoleFamily || "",
  seniority: cp.seniority || "",
  yearsOfExperience: cp.yearsOfExperience ?? 0,
  experienceSummary: cp.experienceSummary || "",
  preferredLocations: (cp.preferredLocations || []).join(", "),
  openToRelocate: !!cp.openToRelocate,
  openToRemote: cp.openToRemote !== false,
  availability: cp.availability || "",
  skills: cp.skills || [],
  links: { ...EMPTY_LINKS, ...(cp.links || {}) },
});

const CareerProfileCard = ({ profile, onSaved, readOnly = false }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(() => toForm(profile));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const startEdit = () => {
    setForm(toForm(profile));
    setError("");
    setEditing(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const { portfolio } = await saveCareerProfile({
        ...form,
        // The comma-separated input is a convenience; the API takes an array.
        preferredLocations: form.preferredLocations
          .split(",").map((s) => s.trim()).filter(Boolean),
      });
      onSaved(portfolio, "Career profile saved.");
      setEditing(false);
    } catch (err) {
      setError(errorText(err, "Failed to save your career profile."));
    } finally {
      setSaving(false);
    }
  };

  const cp = profile || {};
  const hasTarget = !!cp.targetRole;
  const skills = cp.skills || [];
  const links = LINK_FIELDS.filter((f) => cp.links?.[f.key]);

  /* ── Read mode ──────────────────────────────────────────────────────────── */
  if (!editing) {
    return (
      <AppCard>
        <AppCardHeader className="tw-flex-row tw-items-start tw-justify-between tw-gap-3">
          <div className="tw-min-w-0">
            <AppCardTitle className="[&_svg]:tw-text-primary">
              <Briefcase size={18} /> Career Profile
            </AppCardTitle>
            <p className="tw-m-0 tw-mt-1 tw-text-[0.8rem] tw-leading-relaxed tw-text-content-muted">
              What you are aiming for. The resume analysis reads this to score your CV against
              the job you actually want.
            </p>
          </div>
          {!readOnly && (
            <AppButton variant="outline" size="sm" onClick={startEdit}>
              <Edit3 size={14} /> Edit
            </AppButton>
          )}
        </AppCardHeader>

        <AppCardContent className="tw-flex tw-flex-col tw-gap-5">
          {!hasTarget && !readOnly && (
            <Callout tone="info">
              <span>
                <strong>Name the role you are applying for.</strong> Without a target role the
                resume analysis can only check formatting. With one it tells you which skills
                the role expects that your CV never mentions.
              </span>
            </Callout>
          )}

          {cp.headline && (
            <p className="tw-m-0 tw-text-base tw-font-semibold tw-text-content">{cp.headline}</p>
          )}
          {cp.summary && (
            <p className="tw-m-0 tw-text-[0.875rem] tw-leading-relaxed tw-text-content-muted">{cp.summary}</p>
          )}

          <div className="tw-grid tw-gap-x-6 tw-gap-y-4 min-[560px]:tw-grid-cols-2">
            <Field label="Target role" value={cp.targetRole} highlight />
            <Field label="Role family" value={labelFor(ROLE_FAMILIES, cp.targetRoleFamily)} />
            <Field label="Applying at" value={labelFor(SENIORITY_LEVELS, cp.seniority)} />
            <Field label="Experience" value={cp.yearsOfExperience ? `${cp.yearsOfExperience} year${cp.yearsOfExperience === 1 ? "" : "s"}` : "Fresher"} />
            <Field label="Availability" value={cp.availability} />
            <Field
              label="Work preference"
              value={[
                cp.openToRemote && "Open to remote",
                cp.openToRelocate && "Open to relocation",
              ].filter(Boolean).join(" · ")}
            />
          </div>

          {cp.preferredLocations?.length > 0 && (
            <div>
              <FieldLabel>Preferred locations</FieldLabel>
              <div className="tw-mt-1.5 tw-flex tw-flex-wrap tw-gap-1.5">
                {cp.preferredLocations.map((l) => (
                  <AppBadge key={l} variant="secondary" size="sm">
                    <MapPin size={11} className="tw-mr-1 tw-inline" />{l}
                  </AppBadge>
                ))}
              </div>
            </div>
          )}

          {cp.experienceSummary && (
            <div>
              <FieldLabel>Experience</FieldLabel>
              <p className="tw-m-0 tw-mt-1.5 tw-whitespace-pre-line tw-text-[0.875rem] tw-leading-relaxed tw-text-content-muted">
                {cp.experienceSummary}
              </p>
            </div>
          )}

          <div>
            <FieldLabel>
              Skills {skills.length > 0 && <span className="tw-text-content-subtle">({skills.length})</span>}
            </FieldLabel>
            {skills.length === 0 ? (
              <p className="tw-m-0 tw-mt-1.5 tw-text-[0.8rem] tw-text-content-subtle">
                No skills listed yet.
              </p>
            ) : (
              <div className="tw-mt-1.5 tw-flex tw-flex-wrap tw-gap-1.5">
                {skills.map((s, i) => (
                  <span
                    key={`${s.name}-${i}`}
                    className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-solid tw-border-primary/25 tw-bg-primary/[0.07] tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-text-primary"
                    title={`${s.level}${s.years ? ` · ${s.years} year${s.years === 1 ? "" : "s"}` : ""}`}
                  >
                    {s.name}
                    <span className="tw-text-[0.65rem] tw-font-normal tw-opacity-70">{s.level}</span>
                  </span>
                ))}
              </div>
            )}
          </div>

          {links.length > 0 && (
            <div>
              <FieldLabel>Links</FieldLabel>
              <div className="tw-mt-1.5 tw-flex tw-flex-wrap tw-gap-2">
                {links.map(({ key, label, icon }) => (
                  <NavChip key={key} href={cp.links[key]} icon={icon} shape="chip">
                    {label}
                  </NavChip>
                ))}
              </div>
            </div>
          )}
        </AppCardContent>
      </AppCard>
    );
  }

  /* ── Edit mode ──────────────────────────────────────────────────────────── */
  return (
    <AppCard>
      <AppCardHeader>
        <AppCardTitle className="[&_svg]:tw-text-primary">
          <Briefcase size={18} /> Career Profile
        </AppCardTitle>
      </AppCardHeader>
      <AppCardContent>
        <form className="tw-flex tw-flex-col tw-gap-5" onSubmit={submit}>
          {error && <Callout tone="danger" role="alert">{error}</Callout>}

          <div className="tw-rounded-sm tw-border tw-border-solid tw-border-primary/25 tw-bg-primary/[0.05] tw-p-3.5">
            <p className="tw-m-0 tw-flex tw-gap-2 tw-text-[0.8rem] tw-leading-relaxed tw-text-content">
              <Sparkles size={15} className="tw-mt-0.5 tw-shrink-0 tw-text-primary" />
              <span>
                The three fields below — target role, level and skills — are what the AI resume
                analysis grades against. Fill them in before you run it.
              </span>
            </p>
          </div>

          <div className="tw-grid tw-gap-4 min-[560px]:tw-grid-cols-2">
            <FormField
              label="Target role"
              hint="The job title you are applying for"
            >
              <Input
                value={form.targetRole}
                onChange={(e) => set({ targetRole: e.target.value })}
                placeholder="e.g. Backend Engineer, Data Analyst"
                maxLength={120}
              />
            </FormField>
            <FormField label="Role family">
              <NativeSelect
                value={form.targetRoleFamily}
                onChange={(e) => set({ targetRoleFamily: e.target.value })}
                options={ROLE_FAMILIES}
              />
            </FormField>
            <FormField label="Applying at level">
              <NativeSelect
                value={form.seniority}
                onChange={(e) => set({ seniority: e.target.value })}
                options={SENIORITY_LEVELS}
              />
            </FormField>
            <FormField label="Years of experience" hint="0 if you are a fresher">
              <Input
                type="number" min={0} max={60}
                value={form.yearsOfExperience}
                onChange={(e) => set({ yearsOfExperience: Number(e.target.value) || 0 })}
              />
            </FormField>
          </div>

          <FormField label="Headline" hint="One line, the way it would sit at the top of your CV">
            <Input
              value={form.headline}
              onChange={(e) => set({ headline: e.target.value })}
              placeholder="e.g. Final-year CS student building distributed systems"
              maxLength={160}
            />
          </FormField>

          <FormField label="About you" hint="A short paragraph a recruiter would read first">
            <Textarea
              rows={3}
              value={form.summary}
              onChange={(e) => set({ summary: e.target.value })}
              maxLength={2000}
            />
          </FormField>

          <FormField
            label="Your experience"
            hint="Internships, freelance work, part-time roles — in your own words. Two years of internships and two years of production ownership read very differently."
          >
            <Textarea
              rows={4}
              value={form.experienceSummary}
              onChange={(e) => set({ experienceSummary: e.target.value })}
              maxLength={4000}
            />
          </FormField>

          <FormField label="Skills">
            <SkillsField value={form.skills} onChange={(skills) => set({ skills })} />
          </FormField>

          <div className="tw-grid tw-gap-4 min-[560px]:tw-grid-cols-2">
            <FormField label="Preferred locations" hint="Comma separated">
              <Input
                value={form.preferredLocations}
                onChange={(e) => set({ preferredLocations: e.target.value })}
                placeholder="Bengaluru, Hyderabad, Pune"
              />
            </FormField>
            <FormField label="Availability">
              <Input
                value={form.availability}
                onChange={(e) => set({ availability: e.target.value })}
                placeholder="e.g. Immediately, 30 days, after June 2027"
                maxLength={80}
              />
            </FormField>
          </div>

          <div className="tw-flex tw-flex-wrap tw-gap-x-6 tw-gap-y-2">
            <Checkbox
              label="Open to remote work"
              checked={form.openToRemote}
              onChange={(e) => set({ openToRemote: e.target.checked })}
            />
            <Checkbox
              label="Open to relocating"
              checked={form.openToRelocate}
              onChange={(e) => set({ openToRelocate: e.target.checked })}
            />
          </div>

          <div>
            <h4 className="tw-m-0 tw-mb-3 tw-text-sm tw-font-semibold tw-text-content">Links</h4>
            <div className="tw-grid tw-gap-4 min-[560px]:tw-grid-cols-2">
              {LINK_FIELDS.map(({ key, label, icon: Icon, placeholder }) => (
                <FormField
                  key={key}
                  label={<span className="tw-inline-flex tw-items-center tw-gap-1.5"><Icon size={13} /> {label}</span>}
                >
                  <Input
                    type="url"
                    value={form.links[key]}
                    onChange={(e) => set({ links: { ...form.links, [key]: e.target.value } })}
                    placeholder={placeholder}
                  />
                </FormField>
              ))}
            </div>
          </div>

          <div className="tw-flex tw-flex-wrap tw-gap-2">
            <AppButton type="submit" disabled={saving}>
              <Save size={14} /> {saving ? "Saving…" : "Save profile"}
            </AppButton>
            <AppButton type="button" variant="secondary" onClick={() => setEditing(false)} disabled={saving}>
              <X size={14} /> Cancel
            </AppButton>
          </div>
        </form>
      </AppCardContent>
    </AppCard>
  );
};

/** A read-mode label. Uppercase is applied here, never typed into the string. */
const FieldLabel = ({ children }) => (
  <span className="tw-text-[0.7rem] tw-font-semibold tw-uppercase tw-tracking-caps tw-text-content-subtle">
    {children}
  </span>
);

const Field = ({ label, value, highlight = false }) => (
  <div className="tw-min-w-0">
    <FieldLabel>{label}</FieldLabel>
    <p className={`tw-m-0 tw-mt-1 tw-text-[0.875rem] ${highlight ? "tw-font-semibold tw-text-primary" : "tw-text-content"}`}>
      {value || "—"}
    </p>
  </div>
);

export default CareerProfileCard;
